<template>
    <div class="crime-profile" ref="root">
        <div v-if="state === 'loading'" class="placeholder-glow" aria-hidden="true">
            <div class="row g-4">
                <div class="col-lg-6">
                    <span class="placeholder col-5 mb-2"></span>
                    <span class="placeholder crime-profile__placeholder w-100 rounded mb-3"></span>
                    <span class="placeholder col-8 mb-2"></span>
                    <span class="placeholder col-6"></span>
                </div>
                <div class="col-lg-6">
                    <span class="placeholder col-7 mb-2"></span>
                    <span class="placeholder crime-profile__placeholder w-100 rounded mb-3"></span>
                    <span class="placeholder col-9 mb-2"></span>
                    <span class="placeholder col-8"></span>
                </div>
            </div>
        </div>
        <p v-else-if="state === 'failed'" class="text-muted mb-0">Údaje o kriminalitě se nepodařilo načíst.</p>
        <p v-else-if="area === null" class="text-muted mb-0">Pro místo nemovitosti nemáme údaje o kriminalitě.</p>
        <template v-else>
            <div class="row g-4">
                <div class="col-lg-6">
                    <div v-html="head"></div>
                    <div v-if="hero !== ''" class="mb-3" v-html="hero"></div>
                    <div v-html="facts"></div>
                </div>
                <div v-if="breakdown !== ''" class="col-lg-6" v-html="breakdown"></div>
            </div>
            <div class="border-top mt-4 pt-3">
                <div class="row g-4 align-items-center">
                    <div class="col-lg-8">
                        <ul class="crime-profile__help list-unstyled row g-2 mb-2">
                            <li v-for="item in help" :key="item.lead" class="col-md-6">
                                <strong>{{ item.lead }}</strong> {{ item.text }}
                            </li>
                        </ul>
                        <div v-html="attribution"></div>
                    </div>
                    <div class="col-lg-4">
                        <div class="crime-profile__map-cta">
                            <div class="crime-profile__map-cta-title">Jak vypadá kriminalita kolem?</div>
                            <div class="crime-details__meta mb-3">
                                Index sousedních oblastí, jednotlivé skutky za posledních 12 měsíců a letecký snímek místa.
                            </div>
                            <button v-if="unfoldMapId !== null" type="button" class="btn btn-primary" data-bs-toggle="collapse"
                                    :data-bs-target="'#' + unfoldMapId" :aria-controls="unfoldMapId" aria-expanded="false">
                                <i class="fas fa-map-marker-alt me-2"></i>Zobrazit mapu kriminality okolí
                            </button>
                            <button v-else type="button" class="btn btn-primary" @click="openMap">
                                <i class="fas fa-map-marker-alt me-2"></i>Otevřít mapu kriminality okolí
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import {
    attributionContent,
    categoriesTable,
    CrimeArea,
    detailsHead,
    detailsHero,
    detailsNote,
    detailsPoliceIndex,
    detailsRates,
    showsBreakdown,
    trendBars,
} from '@/components/crime/crimePanel';
import {loadCrimeProfile} from '@/model/crime/crimeProfile';
import naja from 'naja';
import {whenNearViewport} from '@/shared/nearViewport';
import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

type LoadState = 'loading' | 'loaded' | 'failed';

// How the figures come about, in the words of the panel: what the index is, where the data come
// from, what happens where there is little of it, and what the comparisons are.
const HELP: ReadonlyArray<{lead: string; text: string}> = [
    {
        lead: 'Index kriminality',
        text: 'Násilné činy, vloupání, krádeže vozidel a ostatní krádeže na 1 000 obyvatel a rok v základní sídelní jednotce, '
            + 'vztažené k průměru ČR (ČR = 100). Do 50 velmi nízká, 50–80 nízká, 80–125 průměrná, 125–200 zvýšená, nad 200 vysoká.',
    },
    {
        lead: 'Odkud jsou data',
        text: 'Otevřená data Policie ČR o skutcích za poslední tři roky (období je uvedeno u oblasti) a počty obyvatel ze sčítání 2021. '
            + 'Polohu skutků policie anonymizuje na zóny, počty oblastí jsou proto podíly zón a nemusí být celé.',
    },
    {
        lead: 'Málo skutků nebo obyvatel',
        text: 'Kde je skutků málo, opírá se index o průměr katastrálního území nebo obce. '
            + 'Kde nikdo nebydlí (pole, lesy, průmyslové zóny), uvádí se místo indexu počet skutků na km² a rok.',
    },
    {
        lead: 'Srovnání',
        text: 'Štítky ukazují, o kolik se míra liší od katastrálního území (KÚ), obce, okresu a ČR. '
            + 'Index kriminality Policie ČR je jiná míra: všechny trestné činy na 10 000 obyvatel, zveřejňovaná jen za obce a okresy.',
    },
];

/**
 * The crime profile of the neighbourhood of a place without a map: the details panel of the crime
 * map laid out on the page in two columns (the index with the rates and comparisons; the yearly
 * slices and categories), with a short explanation of the figures and a call to open the crime
 * map of the neighbourhood: in a dialog, or, where the widget itself sits in a dialog, by
 * unfolding the map (a collapse of the given id) under the widget.
 */
export default defineComponent({
    props: {
        // The place whose neighbourhood is described.
        place: {
            type: Object as PropType<{latitude: number; longitude: number}>,
            required: true,
        },
        openMapUrl: {
            type: String,
            default: null,
        },
        unfoldMapId: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        const state = ref<LoadState>('loading');
        const area = ref<CrimeArea | null>(null);

        const head = computed((): string => (area.value !== null ? detailsHead(area.value) : ''));
        const hero = computed((): string => (area.value !== null ? detailsHero(area.value) : ''));
        const facts = computed((): string => (area.value !== null
            ? detailsRates(area.value) + detailsNote(area.value) + detailsPoliceIndex(area.value)
            : ''));
        const breakdown = computed((): string => {
            if (area.value === null || !showsBreakdown(area.value)) {
                return '';
            }
            // Each in a box of its own, so that the column does not read as a blank area.
            const trend = trendBars(area.value);
            const categories = categoriesTable(area.value);
            return (trend !== '' ? `<div class="crime-profile__panel">${trend}</div>` : '')
                + (categories !== ''
                    ? `<div class="crime-profile__panel"><div class="crime-profile__panel-title">Skutky podle kategorií</div>${categories}</div>`
                    : '');
        });
        const attribution = attributionContent();

        function openMap(): void {
            if (props.openMapUrl === null) {
                return;
            }
            naja.makeRequest('GET', props.openMapUrl, null, {history: false});
        }

        async function load(): Promise<void> {
            try {
                area.value = await loadCrimeProfile(props.place.latitude, props.place.longitude);
                state.value = 'loaded';
            } catch {
                state.value = 'failed';
            }
        }

        // Loaded only once the widget is about to come into view: most visitors of a page never
        // scroll down to it.
        const root = ref<HTMLElement | null>(null);
        let stopWatchingViewport: (() => void) | null = null;

        onMounted(() => {
            stopWatchingViewport = whenNearViewport(root.value!, load);
        });

        onBeforeUnmount(() => stopWatchingViewport?.());

        return {root, state, area, head, hero, facts, breakdown, attribution, help: HELP, openMap};
    },
});
</script>
